import { WSElement, WSSectionToolbar } from "@wingspanhq/fe-component-library";
import {
  PayerEngagement,
  PayerRow,
  PayStatement,
  WorkLog,
  WorkLogConvertedResourceType
} from "@wingspanhq/wingspan-api-sdk";
import { useHistory } from "react-router-dom";
import { queriesInvoices } from "@query";
import { DataView } from "../../../shared/components/DataView";
import { selectorSearchRowName } from "../../../shared/selectors/selectorSearchRowName";
import { getPayerDetailsUrl } from "../../Payers/paths";
import { getWorkLogPayeeStatusCellProps } from "../utils/getWorkLogPayeeStatusCellProps";
import styles from "./WorkLogAssignmentInfo.module.scss";

type Props = {
  shimmer?: boolean;
  workLog?: WorkLog;
  /** The client's search row — its `profile.resolvedName` is the client name
   * every list and picker in the app shows for the same client. */
  payerRow?: PayerRow;
  payerPayeeEngagement?: PayerEngagement;
  /** Already loaded by the route for the paid check and the view link. */
  payStatement?: PayStatement;
};

export const WorkLogAssignmentInfoPayeeView: React.FC<Props> = props => {
  const history = useHistory();

  const invoiceId = props.workLog?.convertedResourceId;
  // A W-2 work log converts into a pay statement; its id is not an invoice id,
  // so only look it up when the work log actually converted into an invoice.
  const isInvoice =
    props.workLog?.convertedResourceType ===
    WorkLogConvertedResourceType.INVOICE;

  const queryInvoice = queriesInvoices.getInvoice.useQuery(
    { invoiceId: invoiceId! },
    { enabled: !!invoiceId && isInvoice }
  );

  // TODO(v3-blocker, WIN-16077): the engagement decides whether an unapproved work log is
  // described as becoming a pay statement or an invoice. SDK 4.2.0 dropped
  // `engagementType` from the payer search row, and the engagement template it
  // now lives on belongs to the payer's account, so a contractor cannot read it.
  // Left unset, which falls back to the 1099 wording.
  const statusCellProps = props.workLog
    ? getWorkLogPayeeStatusCellProps({
        workLog: props.workLog,
        invoice: queryInvoice.data,
        payStatement: props.payStatement
      })
    : undefined;

  const name = props.payerRow
    ? selectorSearchRowName(props.payerRow)
    : undefined;
  const payerId = props.payerRow?.id;

  return (
    <WSElement>
      <WSSectionToolbar title="Engagement information" />
      <DataView
        shimmer={props.shimmer}
        data={[
          {
            label: "Engagement name",
            value: props.payerPayeeEngagement?.engagementName ?? undefined
          },
          {
            label: "Status",
            secondaryText:
              statusCellProps?.text || statusCellProps?.secondaryText,
            secondaryTextColor: statusCellProps?.secondaryTextColor,
            pill: statusCellProps?.pill,
            hideValue: true
          },
          {
            label: "Client name",
            value: name && payerId ? "" : name,
            action:
              name && payerId
                ? {
                    label: name,
                    onClick: () => history.push(getPayerDetailsUrl(payerId)),
                    className: styles.entityLink
                  }
                : undefined
          }
        ]}
      />
    </WSElement>
  );
};
